import { useEffect, useState } from 'preact/hooks';

import { resolvePicto } from './art.js';
import { PictoImg } from './PictoImg.js';
import styles from './Avatar.module.css';
import { cx, memberStyle } from './color.js';

export { COLOR_VAR } from './color.js';

// Gezichten of initialen (oprichter 6 okt 2026, docs/ux/quick-wins): dierenavatars horen alleen bij
// het thema Kinderen op het bord en bij een kind dat in zijn eigen profiel een dier koos. Overal
// anders een initiaal in een neutrale cirkel met de ledenkleur als dunne ring (nooit als vlak).
// Het bord zet dit per thema (apps/kiosk boardTheme.ts); de ouderapp laat het uit en vraagt een
// gezicht alleen in de kindcontext (prop `face`). Een kleine lijst luisteraars zoals de
// pictogrammenset (art.ts): ook lagen buiten de boom van het bord wisselen mee.
let faces = false;
const faceListeners: Array<() => void> = [];

/** Tonen avatars nu hun dier (thema Kinderen)? */
export function avatarFaces(): boolean {
  return faces;
}

/** Dieren aan (thema Kinderen) of uit (initialen); alle getoonde avatars tekenen opnieuw. */
export function setAvatarFaces(on: boolean): void {
  if (on === faces) return;
  faces = on;
  for (const l of faceListeners.slice()) l();
}

function useAvatarFaces(): boolean {
  const [on, setOn] = useState(faces);
  useEffect(() => {
    if (faces !== on) setOn(faces);
    const l = () => setOn(faces);
    faceListeners.push(l);
    return () => {
      const i = faceListeners.indexOf(l);
      if (i >= 0) faceListeners.splice(i, 1);
    };
  }, []);
  return on;
}

export type AvatarSize = 'chip' | 'small' | 'medium' | 'default' | 'kid';

const PX: Record<AvatarSize, number> = { chip: 32, small: 40, medium: 56, default: 72, kid: 100 };

export interface AvatarProps {
  name: string;
  /** Okabe–Ito-sleutel (packages/domain ColorKey). */
  colorKey: string;
  /** Pictogram-id (bijv. 'fox'); zonder icoon toont de avatar de initiaal. */
  icon?: string | null;
  /** Geselecteerd lid (ledenrail/kiezer): ring in de volle lidkleur. */
  selected?: boolean;
  /** chip 32 · small 40 · medium 56 · default 72 · kid 100 px. */
  size?: AvatarSize;
  /** Afwijkende zijde in px (bv. 24 in een kleine chip); de typografie volgt `size`. */
  px?: number;
  /** Decoratief (naam staat er al naast als tekst). */
  decorative?: boolean;
  /**
   * Toon het dier ook buiten het thema Kinderen: alleen in de kindcontext (een kind koos zijn
   * eigen dier, bv. het kindprofiel in de ouderapp).
   */
  face?: boolean;
}

/**
 * Avatar (ONTWERP.md §9). Standaard een initiaal (gewicht 600) in een neutrale cirkel met een dunne
 * ring in de ledenkleur. Met gezichten aan (thema Kinderen of `face`): zacht lidkleurvlak met het
 * dierpictogram of de initiaal in de donkere lid-ink, zoals altijd.
 */
export function Avatar({
  name,
  colorKey,
  icon,
  selected,
  size = 'default',
  px: pxOverride,
  decorative,
  face,
}: AvatarProps) {
  const px = pxOverride || PX[size] || 72;
  const withFace = useAvatarFaces() || face === true;
  const picto = withFace ? resolvePicto(icon) : null;
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  return (
    <span
      class={cx(
        styles.avatar,
        styles[size],
        !withFace && styles.plain,
        selected && styles.selected,
      )}
      style={`${memberStyle(colorKey)};width:${px}px;height:${px}px`}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : name}
      aria-hidden={decorative ? 'true' : undefined}
      data-color={colorKey}
    >
      {picto ? (
        <PictoImg id={picto} class={styles.picto} size={Math.round(px * 0.72)} />
      ) : (
        <span class={styles.initial} aria-hidden="true">
          {initial}
        </span>
      )}
    </span>
  );
}
